// The laptop's display: a quad at the room's laptopScreen anchor that shows
// whatever canvas it is given, and maps a ray hit back to pixels on it.

import * as THREE from "three";
import type { RoomManifest } from "../room/contract.ts";
import { LAYER_SELECT } from "./device.ts";
import { face } from "./type.ts";

export class LaptopScreen {
  readonly mesh: THREE.Mesh;
  /** A box around the whole machine, so clicking the keyboard also selects it. */
  readonly hitbox: THREE.Mesh;
  readonly centre = new THREE.Vector3();
  private texture: THREE.CanvasTexture;
  private canvas: HTMLCanvasElement;
  readonly resolution: [number, number];

  constructor(manifest: RoomManifest) {
    const { corners, resolution } = manifest.anchors.laptopScreen;
    this.resolution = resolution;
    const [bl, br, tr, tl] = corners.map((corner) => new THREE.Vector3().fromArray(corner));
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute([...bl!.toArray(), ...br!.toArray(), ...tr!.toArray(), ...tl!.toArray()], 3));
    // Canvas rows run top to bottom, so v is flipped against the quad.
    geometry.setAttribute("uv", new THREE.Float32BufferAttribute([0, 1, 1, 1, 1, 0, 0, 0], 2));
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    this.canvas = this.bootCanvas();
    this.texture = this.wrap(this.canvas);
    this.mesh = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({
      map: this.texture, toneMapped: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
    }));
    this.mesh.name = "laptop-screen";
    this.centre.copy(bl!).add(br!).add(tr!).add(tl!).multiplyScalar(0.25);

    const width = bl!.distanceTo(br!);
    const height = bl!.distanceTo(tl!);
    const right = new THREE.Vector3().subVectors(br!, bl!).normalize();
    const base = new THREE.Vector3().addVectors(bl!, br!).multiplyScalar(0.5);
    const forward = new THREE.Vector3().crossVectors(right, new THREE.Vector3(0, 1, 0)).multiplyScalar(-1).normalize();
    this.hitbox = new THREE.Mesh(new THREE.BoxGeometry(width * 1.3, height * 1.25, height * 1.5), new THREE.MeshBasicMaterial());
    this.hitbox.position.copy(base).addScaledVector(forward, height * 0.55).add(new THREE.Vector3(0, height * 0.5, 0));
    this.hitbox.rotation.y = Math.atan2(right.z, right.x) * -1;
    this.hitbox.layers.set(LAYER_SELECT);
    this.hitbox.userData.laptop = this;
  }

  private wrap(canvas: HTMLCanvasElement): THREE.CanvasTexture {
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.flipY = false;
    texture.generateMipmaps = true;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.anisotropy = 8;
    return texture;
  }

  /** What the screen shows until the desktop has booted. */
  private bootCanvas(): HTMLCanvasElement {
    const canvas = document.createElement("canvas");
    [canvas.width, canvas.height] = this.resolution;
    const g = canvas.getContext("2d")!;
    g.fillStyle = "#171226";
    g.fillRect(0, 0, canvas.width, canvas.height);
    g.fillStyle = "#cbbde2";
    g.font = face(700, 30);
    g.textAlign = "center";
    g.fillText("Starting Pocket Shell", canvas.width / 2, canvas.height / 2);
    return canvas;
  }

  /** Show a live canvas (the Pocket Shell desktop). */
  show(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    this.texture.dispose();
    this.texture = this.wrap(canvas);
    (this.mesh.material as THREE.MeshBasicMaterial).map = this.texture;
  }

  refresh(): void {
    this.texture.needsUpdate = true;
  }

  /** Screen pixel under a ray, or null when the ray misses the display. */
  pick(raycaster: THREE.Raycaster): [number, number] | null {
    const hit = raycaster.intersectObject(this.mesh, false)[0];
    if (!hit?.uv) return null;
    return [hit.uv.x * this.resolution[0], hit.uv.y * this.resolution[1]];
  }
}
